/**
 * F&O Trade Analysis — top-level container. Fetches
 * `GET /fno-analysis/{symbol}` via `fnoAnalysisService` on mount and on
 * every `symbol` change, and renders the hero card + 5 breakdown
 * accordions + a separate, explicitly-labeled Historical Validation
 * (backtest) section below.
 *
 * Loading/error handling follows `StrategyRecommendationSection.tsx`'s
 * established pattern for this same parent page (`OptionsAnalystPage`):
 * a full-page spinner on the very first load/symbol change, and a
 * `severity="warning"` alert (never a thrown error) on failure —
 * consistent with every other analytics tab in this dashboard.
 *
 * Unlike AI Insights, this panel does NOT auto-poll — F&O Trade Analysis
 * computes + PERSISTS a fresh snapshot on every GET (see
 * `app.api.v1.fno_analysis`'s router docstring), so auto-refreshing on a
 * timer would silently keep writing new snapshot rows in the background;
 * a manual "Refresh" button lets the trader control when that happens.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import fnoAnalysisService from "@/services/fnoAnalysisService";
import type { FnoTradeAnalysisResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { FnoHeroCard } from "./FnoHeroCard";
import { FnoBreakdownAccordions } from "./FnoBreakdownAccordions";
import { HistoricalValidationSection } from "./HistoricalValidationSection";

export function FnoTradeAnalysisPanel({ symbol }: { symbol: string }) {
  const [data, setData] = useState<FnoTradeAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const symbolRef = useRef(symbol);
  symbolRef.current = symbol;
  // Guards against an older in-flight request (e.g. a slow request for a
  // previously-selected symbol) resolving after a newer one and clobbering
  // the newer snapshot — same convention as `StrategyRecommendationSection`.
  const latestRequestId = useRef(0);

  const fetchAnalysis = useCallback(() => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError(null);
    fnoAnalysisService
      .getFnoTradeAnalysis(symbolRef.current)
      .then((result) => {
        if (latestRequestId.current === requestId) setData(result);
      })
      .catch((err) => {
        if (latestRequestId.current === requestId) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (latestRequestId.current === requestId) setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchAnalysis();
  }, [symbol, fetchAnalysis]);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
        <Typography variant="h6" fontWeight={700}>
          F&amp;O Trade Analysis
        </Typography>
        {data && (
          <Button size="small" onClick={fetchAnalysis} disabled={loading}>
            Refresh
          </Button>
        )}
      </Box>

      {loading && !data ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : error && !data ? (
        <Alert severity="warning">{error}</Alert>
      ) : data ? (
        <Stack spacing={0}>
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <FnoHeroCard data={data} />
          <FnoBreakdownAccordions data={data} />
          <HistoricalValidationSection symbol={symbol} />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No F&amp;O Trade Analysis available yet.
        </Typography>
      )}
    </Box>
  );
}

export default FnoTradeAnalysisPanel;
